import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { Observable, map } from 'rxjs';
import {
  GET_EXPORTABLE_SUBMISSIONS,
  TRIGGER_CLAYTON_GO_TO_NEXT_DATA_SYNC,
  TRIGGER_ENERGY_CREDIT_SUBMISSION_EXPORT,
  TRIGGER_HEAT_PUMP_INVOICE_SYNC,
  DELETE_DATABASE_INSPECTION,
} from '../queries/support.queries';
import {
  DELETE_CACHED_INSPECTION,
  GET_ALL_IN_USE_SERIAL_NUMBERS,
  GET_INSPECTION_PROGRESS,
  GET_ZONES_BY_FACILITY,
  SAVE_INSPECTION_PROGRESS,
} from 'src/app/modules/qbuilt/queries/qbuilt.queries';
import {
  MutationResult,
  UpdateResult,
} from 'src/app/shared/graph-queries/types';

@Injectable({
  providedIn: 'root',
})
export class SupportService {
  constructor(private apollo: Apollo) {}

  getNumberOfExportableSubmissions(): Observable<number> {
    return this.apollo
      .query<any>({
        query: GET_EXPORTABLE_SUBMISSIONS,
      })
      .pipe(
        map((x) => {
          return x.data.GetExportableSubmissions.Count;
        }),
      );
  }

  triggerEnergyCreditSubmissionExport(): Observable<MutationResult> {
    return this.apollo.mutate({
      mutation: TRIGGER_ENERGY_CREDIT_SUBMISSION_EXPORT,
    });
  }

  triggerHeatPumpInvoiceSync(): Observable<MutationResult> {
    return this.apollo.mutate({
      mutation: TRIGGER_HEAT_PUMP_INVOICE_SYNC,
    });
  }
  triggerClaytonGoToNextDataSync(
    facilityId: string,
  ): Observable<MutationResult> {
    return this.apollo.mutate({
      mutation: TRIGGER_CLAYTON_GO_TO_NEXT_DATA_SYNC,
      variables: { facilityId },
    });
  }

  // ── QBuilt Inspection Support ──────────────────────────────────────────────

  getZonesByFacility(
    plantNumber: string,
  ): Observable<{ Id: number; Name: string; HBGFacilityNumber: string }[]> {
    return this.apollo
      .query<any>({
        query: GET_ZONES_BY_FACILITY,
        variables: { userFacility: plantNumber },
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
      })
      .pipe(map((res) => res.data?.GetZoneConfigurationsByFacility ?? []));
  }

  getInspectionProgress(
    pk: string,
  ): Observable<{ Pk: string; InspectionData: string } | null> {
    return this.apollo
      .query<any>({
        query: GET_INSPECTION_PROGRESS,
        variables: { filter: pk },
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
      })
      .pipe(map((res) => res.data?.GetInspectionProgress ?? null));
  }

  saveInspectionProgress(
    pk: string,
    inspectionData: any[],
  ): Observable<MutationResult<UpdateResult>> {
    return this.apollo.mutate({
      mutation: SAVE_INSPECTION_PROGRESS,
      variables: {
        payload: JSON.stringify({ Pk: pk, InspectionData: inspectionData }),
      },
      fetchPolicy: 'no-cache',
    });
  }

  deleteInspection(pk: string): Observable<MutationResult<UpdateResult>> {
    return this.apollo.mutate({
      mutation: DELETE_CACHED_INSPECTION,
      variables: { payload: pk },
      fetchPolicy: 'no-cache',
    });
  }

  getSerialsByFacilityAndZone(
    plantNumber: string,
    zoneId: number,
  ): Observable<string[]> {
    return this.apollo
      .query<any>({
        query: GET_ALL_IN_USE_SERIAL_NUMBERS,
        variables: { userFacility: plantNumber, zoneId: String(zoneId) },
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
      })
      .pipe(
        map((res) =>
          (res.data?.GetAllInUseSerials ?? [])
            .map((s: any) => s.SerialNumber as string)
            .filter((s: string) => !!s)
            .sort(),
        ),
      );
  }

  deleteDatabaseInspection(
    serialNumber: string,
    zoneId: number,
  ): Observable<MutationResult<UpdateResult>> {
    return this.apollo.mutate({
      mutation: DELETE_DATABASE_INSPECTION,
      variables: {
        payload: JSON.stringify({ SerialNumber: serialNumber, ZoneId: zoneId }),
      },
      fetchPolicy: 'no-cache',
    });
  }
}
